import { useEffect, useState } from "react";
import { Megaphone, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";

const fmt = (at) => new Date(at).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" });

const Stat = ({ label, value, sub, id, strong }) => (
  <div className={`rounded-lg py-1.5 ${strong ? "bg-[#e6c56f]/15" : "bg-white/5"}`}>
    <p className={`font-display text-base font-extrabold ${strong ? "text-[#f6e3a6]" : "text-[#efdcae]"}`} data-testid={`support-prompt-stats-${id}`}>{value}</p>
    <p className="text-[9px] leading-tight text-[#cdb88f]">{label}{sub && <><br />{sub}</>}</p>
  </div>
);

export function SupportPromptPush() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/support-prompt").then(({ data }) => setS(data)).catch(() => setS({}));
  useEffect(() => { load(); }, []);
  const push = async () => {
    if (!window.confirm("Premium-Hinweis jetzt allen Nutzern (außer Projekt Unterstützern) im Feed anzeigen?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/support-prompt/push");
      toast.success(`Premium-Hinweis angestoßen – ${data.targets} Nutzer sehen ihn im Feed`);
      await load();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl vip-lux p-4 sm:p-5 border border-[#c99a3c]/50 space-y-3" data-testid="support-prompt-push">
      <div>
        <p className="font-display font-extrabold vip-gold-title flex items-center gap-2"><Megaphone className="w-4 h-4 text-amber-300" /> Premium-Hinweis an alle</p>
        <p className="text-xs text-[#cdb88f] mt-1">Zeigt allen außer Projekt Unterstützern den Premium-Hinweis im Feed – bis sie ihn wegklicken. Wer gerade nicht online ist, sieht ihn beim nächsten Öffnen.</p>
      </div>
      <button onClick={push} disabled={busy} data-testid="support-prompt-push-btn" className="h-11 px-5 rounded-full vip-gold-metal font-extrabold text-sm inline-flex items-center gap-2 active:scale-95 transition-transform disabled:opacity-60">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Megaphone className="w-4 h-4" />} Jetzt für alle aufleuchten lassen
      </button>
      {s?.at && <p className="text-xs text-[#efdcae]" data-testid="support-prompt-push-status">Zuletzt angestoßen: {fmt(s.at)} · schon weggeklickt: {s.seen}</p>}
      {s?.pushes?.length > 0 && (
        <ul className="space-y-2" data-testid="support-prompt-stats">
          {s.pushes.map((p, i) => (
            <li key={p.at} className="rounded-xl bg-white/5 border border-[#c99a3c]/25 p-3" data-testid={`support-prompt-stats-row-${i}`}>
              <p className="text-[11px] font-bold text-[#e6c56f]">{fmt(p.at)} · {p.targets} erreichbar</p>
              <div className="mt-2 grid grid-cols-4 gap-1.5 text-center">
                <Stat label="Gesehen" value={p.seen} id={`seen-${i}`} />
                <Stat label="Mehr Infos" value={p.more} sub={p.seen ? `${Math.round((p.more / p.seen) * 100)} %` : ""} id={`more-${i}`} strong />
                <Stat label="Nein" value={p.no} id={`no-${i}`} />
                <Stat label="Unterstützer" sub="in 30 Tagen" value={p.converted} id={`converted-${i}`} strong />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
